import axios, {
  AxiosError,
  AxiosHeaders,
  InternalAxiosRequestConfig,
} from "axios";

import {
  API_ERROR_MESSAGE,
  API_ENDPOINTS,
  API_HEADERS,
  API_STATUS,
  DEFAULT_API_URL,
} from "@/constants";
import { store } from "@/store";
import { clearMaintenance, showMaintenance } from "@/store/app.slice";
import {
  clearAuth,
  markSessionExpired,
  setAccessToken,
  setRefreshToken,
} from "@/store/auth.slice";
import { normalizeApiError } from "@/utils/api-error";
import {
  getStoredAccessToken,
  getStoredRefreshToken,
  setStoredAccessToken,
  setStoredRefreshToken,
} from "@/utils/auth-storage";
import {
  clearStoredMaintenanceMessage,
  getStoredMaintenanceMessage,
  setStoredMaintenanceMessage,
} from "@/utils/maintenance-storage";

interface RetryRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const baseURL = import.meta.env.VITE_API_URL || DEFAULT_API_URL;

const refreshClient = axios.create({
  baseURL,
  timeout: 10_000,
  withCredentials: true,
});

const AUTH_BYPASS_PATTERNS = [
  "/auth/refresh",
  "/auth/login",
  "/auth/phone/",
  "/auth/register",
  "/auth/forgot-password",
];

const setHeader = (
  headers: InternalAxiosRequestConfig["headers"],
  key: string,
  value?: string | null,
) => {
  if (!headers || !value) {
    return;
  }

  if (headers instanceof AxiosHeaders) {
    headers.set(key, value);
    return;
  }

  (headers as Record<string, string>)[key] = value;
};

const clearSession = (reason: "expired" | "manual" = "manual") => {
  setStoredAccessToken(null);
  setStoredRefreshToken(null);
  if (reason === "expired") {
    store.dispatch(markSessionExpired());
    return;
  }

  store.dispatch(clearAuth());
};

const readAccessTokenFromPayload = (payload: unknown) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const nestedData =
    typeof record.data === "object" && record.data !== null
      ? (record.data as Record<string, unknown>)
      : record;
  const nestedTokens =
    typeof nestedData.tokens === "object" && nestedData.tokens !== null
      ? (nestedData.tokens as Record<string, unknown>)
      : nestedData;

  const accessToken =
    nestedTokens.accessToken ??
    nestedTokens.access_token ??
    nestedData.accessToken ??
    nestedData.access_token;
  return typeof accessToken === "string" ? accessToken : null;
};

const readRefreshTokenFromPayload = (payload: unknown) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const nestedData =
    typeof record.data === "object" && record.data !== null
      ? (record.data as Record<string, unknown>)
      : record;
  const nestedTokens =
    typeof nestedData.tokens === "object" && nestedData.tokens !== null
      ? (nestedData.tokens as Record<string, unknown>)
      : nestedData;

  const refreshToken =
    nestedTokens.refreshToken ??
    nestedTokens.refresh_token ??
    nestedData.refreshToken ??
    nestedData.refresh_token;
  return typeof refreshToken === "string" ? refreshToken : null;
};

let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const refreshToken = getStoredRefreshToken();
      const response = await refreshClient.post("/auth/refresh", {
        refresh_token: refreshToken || undefined,
      });
      const accessToken = readAccessTokenFromPayload(response.data);
      const newRefreshToken = readRefreshTokenFromPayload(response.data);

      if (!accessToken) {
        clearSession("expired");
        return null;
      }

      setStoredAccessToken(accessToken);
      store.dispatch(setAccessToken(accessToken));

      if (newRefreshToken) {
        setStoredRefreshToken(newRefreshToken);
        store.dispatch(setRefreshToken(newRefreshToken));
      }

      return accessToken;
    } catch {
      clearSession("expired");
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const shouldBypassAuthHandling = (url?: string) =>
  Boolean(url && AUTH_BYPASS_PATTERNS.some((pattern) => url.includes(pattern)));

const clearMaintenanceState = () => {
  if (!getStoredMaintenanceMessage()) {
    return;
  }

  clearStoredMaintenanceMessage();
  store.dispatch(clearMaintenance());
};

export const api = axios.create({
  baseURL,
  timeout: 10_000,
  withCredentials: true,
});

export const checkApiHealth = async () => {
  const { data } = await refreshClient.get(API_ENDPOINTS.HEALTH);
  return data;
};

api.interceptors.request.use((config) => {
  const token = getStoredAccessToken();
  const nextConfig = config;

  nextConfig.headers = nextConfig.headers ?? new AxiosHeaders();
  setHeader(
    nextConfig.headers,
    API_HEADERS.AUTHORIZATION,
    token ? `Bearer ${token}` : null,
  );

  return nextConfig;
});

api.interceptors.response.use(
  (response) => {
    clearMaintenanceState();
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryRequestConfig | undefined;
    const normalizedError = normalizeApiError(error);
    const status = normalizedError.status;

    if (status === API_STATUS.UNAUTHORIZED) {
      if (
        !originalRequest ||
        shouldBypassAuthHandling(originalRequest.url) ||
        !getStoredAccessToken()
      ) {
        return Promise.reject(normalizedError);
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true;
        originalRequest.headers = originalRequest.headers ?? new AxiosHeaders();

        const nextToken = await refreshAccessToken();

        if (nextToken) {
          setHeader(
            originalRequest.headers,
            API_HEADERS.AUTHORIZATION,
            `Bearer ${nextToken}`,
          );
          return api(originalRequest);
        }
      }

      // If retry is already true or refresh returned null
      clearSession("expired");
      return Promise.reject(normalizedError);
    }

    if (status === API_STATUS.MAINTENANCE) {
      const maintenanceMessage =
        normalizedError.message || API_ERROR_MESSAGE.MAINTENANCE;
      setStoredMaintenanceMessage(maintenanceMessage);
      store.dispatch(
        showMaintenance({
          message: maintenanceMessage,
        }),
      );
    }

    return Promise.reject(normalizedError);
  },
);

export default api;
